"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";

type SearchResult = {
  title: string;
  excerpt: string;
  href: string;
  score?: number;
};

// 高亮关键词的函数
function highlightText(text: string, query: string): string {
  if (!query.trim()) return text;
  
  // 对于中文，不按空格分词，而是直接匹配整个查询词
  // 同时也支持英文的空格分词
  const words = [];
  
  // 如果查询包含中文字符，直接使用整个查询词
  if (/[\u4e00-\u9fff]/.test(query)) {
    words.push(query.trim());
    // 同时也添加按空格分割的词（支持混合搜索）
    const spaceWords = query.trim().split(/\s+/).filter(word => word.length > 0);
    words.push(...spaceWords);
  } else {
    // 纯英文按空格分词
    words.push(...query.trim().split(/\s+/).filter(word => word.length > 0));
  }
  
  // 去重并转义特殊字符
  const uniqueWords = [...new Set(words)].map(word => 
    word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  );
  
  if (uniqueWords.length === 0) return text;
  
  const regex = new RegExp(`(${uniqueWords.join('|')})`, 'gi');
  
  return text.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-800 px-1 rounded">$1</mark>');
}

export function Search() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [isPagefindReady, setIsPagefindReady] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 检查 Pagefind 是否可用
  useEffect(() => {
    const checkPagefind = () => {
      if ((window as any).pagefind) {
        console.log("✅ Pagefind 可用");
        setIsPagefindReady(true);
        return true;
      }
      return false;
    };

    // 立即检查
    if (!checkPagefind()) {
      // 如果不可用，监听加载事件
      const handleLoaded = () => {
        if (checkPagefind()) {
          window.removeEventListener('pagefind-loaded', handleLoaded);
        }
      };
      
      window.addEventListener('pagefind-loaded', handleLoaded);
      
      // 定时检查作为备用方案
      const interval = setInterval(() => {
        if (checkPagefind()) {
          clearInterval(interval);
          window.removeEventListener('pagefind-loaded', handleLoaded);
        }
      }, 500);

      // 5秒后停止检查
      setTimeout(() => {
        clearInterval(interval);
        window.removeEventListener('pagefind-loaded', handleLoaded);
      }, 5000);
    }
  }, []);

  // 转换 Pagefind URL 到正确的路由
  const convertPagefindUrl = (url: string): string => {
    console.log("原始 URL:", url);
    
    // 处理各种可能的 Pagefind 生成路径
    let converted = url;
    
    // 移除 .html 扩展名
    if (converted.endsWith('.html')) {
      converted = converted.replace(/\.html$/, '');
    }
    
    // 处理 Next.js 构建目录结构
    if (converted.includes('/server/app/')) {
      // 将 /server/app/blog/article-name 转换为 /blog/article-name
      converted = converted.replace(/^\/server\/app/, '');
    } else if (converted.includes('/server/pages/')) {
      // 处理 pages 目录
      converted = converted.replace(/^\/server\/pages/, '');
    } else if (converted.startsWith('/app/')) {
      // 将 /app/blog/article-name 转换为 /blog/article-name
      converted = converted.replace(/^\/app/, '');
    }
    
    // 确保以 / 开头
    if (!converted.startsWith('/')) {
      converted = '/' + converted;
    }
    
    // 处理根页面
    if (converted === '/page' || converted === '/layout') {
      converted = '/';
    }
    
    // 移除可能的重复路径
    converted = converted.replace(/\/+/g, '/');
    
    console.log("转换后 URL:", converted);
    return converted;
  };

  // 执行搜索
  const doSearch = async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    if (!isPagefindReady || !(window as any).pagefind) {
      console.log("Pagefind 未准备就绪");
      return;
    }

    setLoading(true);
    
    try {
      console.log("搜索:", searchQuery);
      const response = await (window as any).pagefind.search(searchQuery);
      console.log("搜索结果:", response);
      
      if (!response || !response.results || response.results.length === 0) {
        setResults([]);
        return;
      }

      // 获取前 10 个结果的详细数据
      const details = await Promise.all(
        response.results.slice(0, 10).map(async (result: any) => {
          try {
            const data = await result.data();
            console.log("完整结果数据:", data);
            console.log("URL:", data.url);
            console.log("原始标题:", data.meta?.title);
            console.log("内容片段:", data.excerpt);
            console.log("完整内容（前200字符）:", (data.content || '').substring(0, 200));
            console.log("原始内容（前200字符）:", (data.raw_content || '').substring(0, 200));
            
            const convertedUrl = convertPagefindUrl(data.url || '#');
            
            // 改进标题提取逻辑
            let title = '无标题';
            let cleanExcerpt = data.excerpt || '';
            
            // 1. 优先使用meta标题，现在看起来已经是正确的了
            if (data.meta?.title && data.meta.title !== 'My Blog') {
              title = data.meta.title;
              // 移除可能的站点后缀
              title = title.replace(/\s*\|\s*My Blog\s*$/i, '');
              console.log("从 meta 提取标题:", title);
            }
            
            // 2. 如果meta标题不可用，从content开头提取
            if (title === '无标题' && data.content) {
              const content = data.content;
              
              // 直接从content开头提取第一行作为标题
              const firstLine = content.trim().split(/[.\n]/)[0];
              if (firstLine && firstLine.length > 3 && firstLine.length < 150) {
                title = firstLine.trim();
                console.log("从content开头提取标题:", title);
              }
              
              // 如果第一行太短，尝试第一个句子
              if (title.length < 10) {
                const firstSentence = content.trim().match(/^([^.。!！?？]{5,100}[.。!！?？]?)/);
                if (firstSentence && firstSentence[1]) {
                  title = firstSentence[1].trim().replace(/[.。!！?？]$/, '');
                  console.log("从content第一句提取标题:", title);
                }
              }
            }
            
            // 清理标题
            title = title.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
            
            // 清理excerpt - 移除侧边栏内容
            cleanExcerpt = cleanExcerpt
              .replace(/^My Blog[\s\S]*?Explore content and articles[.\s]*/, '') // 移除侧边栏开头
              .replace(/My Blog\s*Explore content and articles\.?\s*/, '') // 移除其他位置的侧边栏文本
              .replace(/Home\s*Blog\s*Projects\s*Work\s*/, '') // 移除导航菜单
              .replace(/Article [0-9]+\s*/, '') // 移除文章编号
              .replace(/^\s*[\s\S]*?(?=[A-Z][a-z].*?[.!?]|$)/, '') // 移除开头的杂乱文本，保留主要内容
              .trim();
            
            // 如果excerpt太短，尝试从content提取更好的描述
            if (cleanExcerpt.length < 50 && data.content) {
              const contentMatch = data.content.match(/<p[^>]*>(.*?)<\/p>/i);
              if (contentMatch) {
                cleanExcerpt = contentMatch[1].replace(/<[^>]*>/g, '').trim();
              }
            }
            
            console.log("最终标题:", title);
            console.log("清理后的excerpt:", cleanExcerpt);
            
            return {
              title: title || '无标题',
              excerpt: cleanExcerpt,
              href: convertedUrl,
              score: result.score
            };
          } catch (error) {
            console.error('获取结果详情失败:', error);
            return null;
          }
        })
      );

      const validResults = details.filter(Boolean) as SearchResult[];
      console.log("最终结果:", validResults);
      setResults(validResults);
    } catch (error) {
      console.error('搜索错误:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // 处理输入变化
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    
    // 简单的防抖
    const timeoutId = setTimeout(() => {
      doSearch(value);
    }, 300);

    // 清理上一个timeout（这里我们不存储它，让它自然执行）
    return () => clearTimeout(timeoutId);
  };

  // Handle click outside to close
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Focus input when search is opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={searchRef}>
      {/* Search button */}
      <button
        onClick={() => setIsOpen(true)}
        className="group relative p-3 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border border-purple-200/50 dark:border-purple-700/50 hover:border-purple-300 dark:hover:border-purple-600 transition-all duration-300 shadow-lg hover:shadow-purple-300/25 dark:hover:shadow-purple-500/25"
        aria-label="Search articles"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-purple-400/20 to-blue-400/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          className="w-5 h-5 relative z-10 text-gray-600 dark:text-gray-300 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-300"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
          />
        </svg>
      </button>

      {/* Search modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4 sm:pt-24">
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-md"
            onClick={() => setIsOpen(false)}
          ></div>
          <div className="relative w-full max-w-2xl bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-2xl shadow-2xl overflow-hidden border border-purple-200/30 dark:border-purple-700/30 animate-in slide-in-from-top-5 duration-300">
            {/* Header with gradient border */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-blue-500/20 to-pink-500/20"></div>
              <div className="relative flex items-center border-b border-purple-200/30 dark:border-purple-700/30 bg-white/50 dark:bg-gray-800/50">
                <div className="flex items-center justify-center w-12 h-12 ml-4">
                  <div className="relative">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                      className="w-5 h-5 text-purple-500"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                      />
                    </svg>
                    <div className="absolute inset-0 bg-purple-500/20 rounded-full blur-sm"></div>
                  </div>
                </div>
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder={isPagefindReady ? "🔍 搜索文章..." : "⏳ 搜索加载中..."}
                  disabled={!isPagefindReady}
                  className="flex-1 p-4 bg-transparent border-0 focus:ring-0 focus:outline-none text-gray-900 dark:text-gray-100 disabled:opacity-50 placeholder-purple-400 dark:placeholder-purple-300 text-lg"
                />
                {loading && (
                  <div className="p-4">
                    <div className="relative w-6 h-6">
                      <div className="absolute inset-0 rounded-full border-2 border-purple-200 dark:border-purple-700"></div>
                      <div className="absolute inset-0 rounded-full border-2 border-purple-500 border-t-transparent animate-spin"></div>
                    </div>
                  </div>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-4 text-gray-500 hover:text-purple-500 dark:hover:text-purple-400 transition-colors duration-200 group"
                  aria-label="Close search"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-5 h-5 group-hover:scale-110 transition-transform duration-200"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Results */}
            <div className="max-h-[60vh] overflow-y-auto p-4">
              {!isPagefindReady ? (
                <div className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-gradient-to-r from-purple-400 to-blue-500 rounded-full">
                    <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-lg">搜索索引加载中...</p>
                </div>
              ) : results.length > 0 ? (
                <div className="space-y-3">
                  {results.map((result, index) => (
                    <div key={result.href + index} className="group">
                      <Link
                        href={result.href}
                        onClick={() => setIsOpen(false)}
                        className="block p-4 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border border-purple-200/30 dark:border-purple-700/30 hover:border-purple-300 dark:hover:border-purple-600 transition-all duration-300 hover:shadow-lg hover:shadow-purple-200/25 dark:hover:shadow-purple-800/25 hover:scale-[1.02] transform"
                      >
                        <div className="relative">
                          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-blue-500/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <div className="relative">
                            <h3 
                              className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-300"
                              dangerouslySetInnerHTML={{ 
                                __html: highlightText(result.title, query) 
                              }}
                            />
                            <p 
                              className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed"
                              dangerouslySetInnerHTML={{ 
                                __html: highlightText(result.excerpt, query) 
                              }}
                            />
                            {result.score && (
                              <div className="flex items-center mt-3 text-xs text-purple-500 dark:text-purple-400">
                                <div className="flex items-center mr-2">
                                  <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full mr-1"></div>
                                  匹配度: {Math.round(result.score * 100)}%
                                </div>
                                <div className="flex-1 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-purple-400 to-pink-400 rounded-full transition-all duration-500"
                                    style={{ width: `${Math.round(result.score * 100)}%` }}
                                  ></div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>
              ) : query.trim() !== "" && !loading ? (
                <div className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-gradient-to-r from-gray-400 to-gray-500 rounded-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                      className="w-8 h-8 text-white"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-lg mb-2">
                    没有找到 "<span className="text-purple-500 font-medium">{query}</span>" 的相关结果
                  </p>
                  <p className="text-gray-500 dark:text-gray-500 text-sm">
                    试试其他关键词或检查拼写
                  </p>
                </div>
              ) : query.trim() === "" ? (
                <div className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                      className="w-8 h-8 text-white"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-lg mb-2">
                    ✨ 输入关键词搜索文章...
                  </p>
                  <p className="text-gray-500 dark:text-gray-500 text-sm">
                    支持中英文搜索，快速找到你想要的内容
                  </p>
                </div>
              ) : null}
            </div>
            
            {/* Bottom decoration */}
            <div className="h-1 bg-gradient-to-r from-purple-500 via-blue-500 to-pink-500"></div>
          </div>
        </div>
      )}
    </div>
  );
}
